import { confirm } from 'devextreme/ui/dialog';
import React, { useState } from 'react';
import { deleteApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
import { Columns } from '../../pages/Assets/Columns/AssetTabMaintenanceCostsColumns';
import { MaintenanceCost, MaintenanceCostType } from '../../types/maintenanceCost.types';
import { API_URL } from '../../utils/apiUrl';
import MaintenanceCostModal from '../modals/MaintenanceCostModal';
import { SisDataGrid } from '../table';
import { IDataGrid } from './AssetsDataGrid';

export interface IAssetTabMaintenanceCostsDataGrid extends IDataGrid {
  assetId: number;
  costTypes?: MaintenanceCostType[];
  currency?: string;
  isReadOnly?: boolean;
  mutateData: () => void;
}

export const AssetTabMaintenanceCostsDataGrid = ({
  assetId,
  data,
  costTypes,
  currency,
  isReadOnly,
  mutateData,
}: IAssetTabMaintenanceCostsDataGrid) => {
  const { translate } = useTranslation();
  const transFormName = 'Asset.MaintenanceCost';
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<MaintenanceCost | null>(null);

  const onToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        html: `${translate!('Maintenance costs', transFormName)}`,
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'add',
          type: 'default',
          stylingMode: 'contained',
          onClick: () => handleNew(),
          visible: !isReadOnly,
        },
      },
    );
  };

  const handleNew = () => {
    setSelectedRowData(null);
    setModalVisible(true);
  };

  const handleEdit = (e: any) => {
    setSelectedRowData(e.data);
    setModalVisible(true);
  };

  /** Smazání nastaví na BE příznak na neplatný, záznam se fyzicky nemaže. */
  const handleRemove = async (e: any) => {
    const id = e?.data?.id;
    if (!id) return;
    if (
      !(await confirm(
        translate!('Are you sure you want to delete maintenance cost?', transFormName),
        translate!('Confirm', ''),
      ))
    )
      return;

    await deleteApi({
      url: API_URL.MAINTENANCE_COST_DELETE,
      id,
      callAfterSuccess: mutateData,
    });
  };

  const handleClose = () => {
    setModalVisible(false);
    setSelectedRowData(null);
  };

  return (
    <>
      <SisDataGrid
        data={data}
        keyExpr={'id'}
        transFormName={transFormName}
        columns={Columns(isReadOnly, currency, translate, handleEdit, handleRemove)}
        onToolbarPreparing={onToolbarPreparing}
        onCellDblClick={(e) => e.row && handleEdit(e.row)}
        columnChooser={false}
        allowColumnResizing={false}
        allowColumnReordering={false}
        enableXlsxExport
        isReadOnly={isReadOnly}
        pageSizeChangeVisible
        totalSummaryEnabled
        totalSummaryColumns={[
          {
            column: 'amount',
            summaryType: 'sum',
            format: { type: 'fixedPoint', precision: 2 },
          },
        ]}
      />
      {isModalVisible && (
        <MaintenanceCostModal
          isVisible={isModalVisible}
          onClose={handleClose}
          onSaved={mutateData}
          assetId={assetId}
          costTypes={costTypes}
          data={selectedRowData}
          currency={currency}
          isReadOnly={isReadOnly}
        />
      )}
    </>
  );
};
